// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ChatPage.test.tsx
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";

import { buildAskV2ProjectReviewPath } from "../paths";

/**
 * Move a chat-scoped review URL (`/ask-puntt/:chatId/reviews/:reviewId`) onto
 * the review's project route once the chat's project has loaded for this
 * viewer. Links built before review links became project-aware, and review
 * cards clicked on a project chat's own page, otherwise open the review with
 * no project beside it.
 *
 * Stays put when the chat has no project or the project does not load (not a
 * member, deleted): the chat route still renders the review on its own.
 *
 * Carries `?compare=1` across, so a comparison link still opens its version
 * comparison on the project route.
 */
export function useAskV2InProjectReviewRedirect(input: {
  projectId: string | null;
  projectLoaded: boolean;
  reviewId?: string;
  commentId?: string;
  compareRequested?: boolean;
}) {
  const navigate = useNavigate();
  const { projectId, projectLoaded, reviewId, commentId, compareRequested } =
    input;
  useEffect(() => {
    if (!reviewId || !projectId || !projectLoaded) return;
    // why: replace, not push -- Back from the project review must not land on
    // the chat URL that would redirect straight back here.
    navigate({
      ...buildAskV2ProjectReviewPath(projectId, reviewId, commentId),
      // why: a navigate with no `search` clears the URL's search, which would
      // drop `?compare=1` before the workspace reads it.
      search: compareRequested ? { compare: true } : {},
      replace: true,
    } as never);
  }, [
    navigate,
    projectId,
    projectLoaded,
    reviewId,
    commentId,
    compareRequested,
  ]);
}
